import { getDataFromLocalDb } from "../../services/dbService";
import { getPricingData } from "../../services/catalogueService";
import { getOutletDetails, getOutletDetailsForSecondary } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { OrderType } from "../../types/types";
import { ChannelkartNetworkGet } from "../../services/networkService";
import { EnvironmentRegistry } from "../../services/registry/environmentRegistry";
import { fetchAllData } from '../../features/collections/services/collectionService';
import { homePageCacheService, CACHE_KEYS, getCachedData, setCachedData, hasCachedData } from "./cacheService";
import axios from "axios";
import { OutletDetails } from "../../features/createSecondaryOrder/models";

interface Api1Response {
  skucode: string;
  outOfStockTime: string;
  outOfStockDays: number;
  avgDailyOrder: number;
  [key: string]: any; 
}

interface EnrichedResponse {
  skucode: string;
  outOfStockTime: string;
  outOfStockDays: number;
  avgDailyOrder: number;
  blobKey: string;
  skuName: string;
  packPtr: number;
  [key: string]: any; 
}

interface BatchPricingResponse {
  skuCode: string;
  packPtr: number;
  [key: string]: any;
}

// Function to get batch pricing data for top SKUs
const getBatchPricingData = async (skuCodes: string[]): Promise<Map<string, any>> => {
  if (!skuCodes || skuCodes.length === 0) {
    return new Map();
  }

  try {
    // Get supplier code from auth context (using loginId) or use default
    const authContext = localStorage.getItem("authContext");
    const user = authContext ? JSON.parse(authContext).user : null;
    const supplierCode = user?.loginId;

    // URL encode the SKU codes
    const encodedSkuCodes = skuCodes.map(code => encodeURIComponent(code)).join('%2C');

    const url = `/productmetadata/v1/supplier/${supplierCode}/batchcodes?batchCodes=${encodedSkuCodes}`;

    const response = await ChannelkartNetworkGet(url);

    const data: BatchPricingResponse[] = response.data;

    // Create a map with SKU code as key and first pricing entry as value
    const pricingMap = new Map<string, any>();

    // Group by SKU code and pick the first entry for each
    const groupedBySku = new Map<string, BatchPricingResponse[]>();
    data.forEach(item => {
      if (item.skuCode) {
        const skuCode = item.skuCode.trim();
        if (!groupedBySku.has(skuCode)) {
          groupedBySku.set(skuCode, []);
        }
        groupedBySku.get(skuCode)!.push(item);
      }
    });

    // Pick the first entry for each SKU
    groupedBySku.forEach((entries, skuCode) => {
      if (entries.length > 0) {
        pricingMap.set(skuCode, entries[0]);
      }
    });

    return pricingMap;
  } catch (error) {
    console.error('Error fetching batch pricing data:', error);
    return new Map();
  }
};

export interface PromoBannerData {
  id: string;
  elementType: string;
  elementTitle: string;
  blobKey: string;
  mediaUrl?: string;
  mediaName?: string;
  fileName?: string;
}

export interface VideoAdData {
  id: string;
  title: string;
  subtitle: string;
  buttonText: string;
  videoUrl: string;
  brand: string;
  logo?: string;
}

export const getBannerData = async (): Promise<{
  promoBanners: PromoBannerData[];
  videoAds: VideoAdData[];
}> => {
  try {
      // Check cache first
  const cachedData = getCachedData<{
    promoBanners: PromoBannerData[];
    videoAds: VideoAdData[];
  }>(CACHE_KEYS.BANNER_DATA);

  if (cachedData) {
    return cachedData;
  }

    const fallbackData: any = {
      "features": {
        "ckcoe": [
          {
            "creationTime": "2025-08-20 16:09:43",
            "lastModifiedTime": "2025-08-20 16:09:43",
            "bannerName": "test image",
            "bannerDescription": "test image",
            "createdBy": "admin@applicate.in",
            "version": 2,
            "extendedAttributes": {
              "distributionData": {
                "banner": "test image",
                "endDate": "2026-01-01 23:59:59",
                "startDate": "2025-08-20 00:00:00",
                "activeStatus": "active"
              }
            },
            "activeStatus": "active",
            "bannerType": "template1",
            "modifiedBy": "admin@applicate.in",
            "id": "0e64892c-952b-429b-814d-87aab987c077",
            "lob": "ckcoeuat",
            "changed": true,
            "bannerElements": [
              {
                "id": "1349f6a9-e4bc-4258-8f0d-6eb179289204",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "1",
                "elementTitle": "Element One",
                "fileName": "1.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoedemo/images/banner1-XaGixSjUrUPA.png",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/mdm2",
                "mediaName": "google"
              },
              {
                "id": "40adca9d-e31d-41ba-97cf-4812254cdb03",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "3",
                "elementTitle": "Element Three",
                "fileName": "3.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoedemo/images/banner3-TnVdHFQquTHu.png",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/adminPanel",
                "mediaName": "google"
              },
              {
                "id": "45ac2bc6-7ec2-46e2-a8ec-92012d523ca6",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "2",
                "elementTitle": "Element Two",
                "fileName": "2.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/ckcoedemo/images/banner2-OkYuBbLKKcnh.png",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/adminPanel",
                "mediaName": "google"
              }
            ]
          },
          {
            "creationTime": "2025-08-20 15:56:19",
            "lastModifiedTime": "2025-08-20 15:56:19",
            "bannerName": "test banner",
            "bannerDescription": "test",
            "createdBy": "admin@applicate.in",
            "version": 1,
            "extendedAttributes": {
              "distributionData": {
                "banner": "test banner",
                "endDate": "2026-01-01 23:59:59",
                "startDate": "2025-08-20 00:00:00",
                "activeStatus": "active"
              }
            },
            "activeStatus": "active",
            "bannerType": "template20",
            "modifiedBy": "admin@applicate.in",
            "id": "7f6541d7-affc-403b-a04a-735c5231bf5e",
            "lob": "ckcoeuat",
            "changed": true,
            "bannerElements": [
              {
                "id": "1c59d1e1-f3a8-4417-9766-3fdeec81508a",
                "elementType": "image",
                "extendedAttributes": {
                  "title": "See why it's a favorite for millions",
                  "subtitle": "30% less dye chemicals, vibrant results!",
                  "buttonText": "Watch Now",
                  "brand": "Indica"
                },
                "activeStatus": "active",
                "elementNumber": "1",
                "elementTitle": "Element One",
                "mediaUrl": "https://youtu.be/rtKp-oFouNE?si=Ec6lKftGzofEcupi",
                "mediaName": "youtubeLink"
              },
              {
                "id": "6c64d0b3-62b6-461a-a631-de5f775335fe",
                "elementType": "image",
                "extendedAttributes": {
                  "title": "CavinKare's Trusted Essentials",
                  "subtitle": "From hair care to grooming, discover top-performing brands loved across India.",
                  "buttonText": "Watch Now",
                  "brand": "CavinKare"
                },
                "activeStatus": "active",
                "elementNumber": "2",
                "elementTitle": "Element Two",
                "mediaUrl": "https://youtu.be/DN2knzWA6IU?si=_TGEUJLayqkBr3Bc",
                "mediaName": "youtubeLink"
              }
            ]
          }
        ],
        "kgbpl": [
          {
            "creationTime": "2025-08-20 16:09:43",
            "lastModifiedTime": "2025-08-20 16:09:43",
            "bannerName": "test image",
            "bannerDescription": "test image",
            "createdBy": "admin@applicate.in",
            "version": 2,
            "extendedAttributes": {
              "distributionData": {
                "banner": "test image",
                "endDate": "2026-01-01 23:59:59",
                "startDate": "2025-08-20 00:00:00",
                "activeStatus": "active"
              }
            },
            "activeStatus": "active",
            "bannerType": "template1",
            "modifiedBy": "admin@applicate.in",
            "id": "0e64892c-952b-429b-814d-87aab987c077",
            "lob": "ckcoeuat",
            "changed": true,
            "bannerElements": [
              {
                "id": "1349f6a9-e4bc-4258-8f0d-6eb179289204",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "1",
                "elementTitle": "Element One",
                "fileName": "1.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/kgbpldemo/images/000_banner1-tCULxgldozCt.jpeg",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/mdm2",
                "mediaName": "google"
              },
              {
                "id": "40adca9d-e31d-41ba-97cf-4812254cdb03",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "3",
                "elementTitle": "Element Three",
                "fileName": "3.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/kgbpldemo/images/000_banner3-NjNiqpolLtxW.jpeg",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/adminPanel",
                "mediaName": "google"
              },
              {
                "id": "45ac2bc6-7ec2-46e2-a8ec-92012d523ca6",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "2",
                "elementTitle": "Element Two",
                "fileName": "2.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/kgbpldemo/images/000_banner2-YdVCfAmvHuMF.jpeg",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/adminPanel",
                "mediaName": "google"
              }
            ]
          },
          {
            "creationTime": "2025-08-20 15:56:19",
            "lastModifiedTime": "2025-08-20 15:56:19",
            "bannerName": "test banner",
            "bannerDescription": "test",
            "createdBy": "admin@applicate.in",
            "version": 1,
            "extendedAttributes": {
              "distributionData": {
                "banner": "test banner",
                "endDate": "2026-01-01 23:59:59",
                "startDate": "2025-08-20 00:00:00",
                "activeStatus": "active"
              }
            },
            "activeStatus": "active",
            "bannerType": "template20",
            "modifiedBy": "admin@applicate.in",
            "id": "7f6541d7-affc-403b-a04a-735c5231bf5e",
            "lob": "ckcoeuat",
            "changed": true,
            "bannerElements": [
              {
                "id": "1c59d1e1-f3a8-4417-9766-3fdeec81508a",
                "elementType": "image",
                "extendedAttributes": {
                  "title": "See why it's a favorite for millions",
                  "subtitle": "30% less dye chemicals, vibrant results!",
                  "buttonText": "Watch Now",
                  "brand": "Indica"
                },
                "activeStatus": "active",
                "elementNumber": "1",
                "elementTitle": "Element One",
                "mediaUrl": "https://www.youtube.com/watch?v=aOJLl9kjO-c",
                "mediaName": "youtubeLink"
              },
              {
                "id": "6c64d0b3-62b6-461a-a631-de5f775335fe",
                "elementType": "image",
                "extendedAttributes": {
                  "title": "CavinKare's Trusted Essentials",
                  "subtitle": "From hair care to grooming, discover top-performing brands loved across India.",
                  "buttonText": "Watch Now",
                  "brand": "CavinKare"
                },
                "activeStatus": "active",
                "elementNumber": "2",
                "elementTitle": "Element Two",
                "mediaUrl": "https://www.youtube.com/watch?v=ItO29hI1bTE",
                "mediaName": "youtubeLink"
              }
            ]
          }
        ],
        "cokesa": [
          {
            "creationTime": "2025-08-20 16:09:43",
            "lastModifiedTime": "2025-08-20 16:09:43",
            "bannerName": "test image",
            "bannerDescription": "test image",
            "createdBy": "admin@applicate.in",
            "version": 2,
            "extendedAttributes": {
              "distributionData": {
                "banner": "test image",
                "endDate": "2026-01-01 23:59:59",
                "startDate": "2025-08-20 00:00:00",
                "activeStatus": "active"
              }
            },
            "activeStatus": "active",
            "bannerType": "template1",
            "modifiedBy": "admin@applicate.in",
            "id": "0e64892c-952b-429b-814d-87aab987c077",
            "lob": "ckcoeuat",
            "changed": true,
            "bannerElements": [
              {
                "id": "1349f6a9-e4bc-4258-8f0d-6eb179289204",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "1",
                "elementTitle": "Element One",
                "fileName": "1.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/cokesademo/images/1-nevNxhaicLhc.jpeg",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/mdm2",
                "mediaName": "google"
              },
              {
                "id": "40adca9d-e31d-41ba-97cf-4812254cdb03",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "3",
                "elementTitle": "Element Three",
                "fileName": "3.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/cokesademo/images/2-ZILZtwofpkiC.jpeg",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/adminPanel",
                "mediaName": "google"
              },
              {
                "id": "45ac2bc6-7ec2-46e2-a8ec-92012d523ca6",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "2",
                "elementTitle": "Element Two",
                "fileName": "2.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/cokesademo/images/7b3b9695-0111-489e-a3c7-6d8783a8589f-yHmmYMNriSrq.jpeg",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/adminPanel",
                "mediaName": "google"
              }
              ,
              {
                "id": "45ac2bc6-7ec2-46e2-a8ec-92012d523ca6",
                "elementType": "image",
                "extendedAttributes": null,
                "activeStatus": "active",
                "elementNumber": "2",
                "elementTitle": "Element Two",
                "fileName": "2.png",
                "blobKey": "https://d2nvw4ekms3xzy.cloudfront.net/cokesademo/images/4-IeFqvUuzIdSv.jpeg",
                "mediaUrl": "https://ckcoeuat.salescode.ai/dashboard/adminPanel",
                "mediaName": "google"
              }
            ]
          },
          {
            "creationTime": "2025-08-20 15:56:19",
            "lastModifiedTime": "2025-08-20 15:56:19",
            "bannerName": "test banner",
            "bannerDescription": "test",
            "createdBy": "admin@applicate.in",
            "version": 1,
            "extendedAttributes": {
              "distributionData": {
                "banner": "test banner",
                "endDate": "2026-01-01 23:59:59",
                "startDate": "2025-08-20 00:00:00",
                "activeStatus": "active"
              }
            },
            "activeStatus": "active",
            "bannerType": "template20",
            "modifiedBy": "admin@applicate.in",
            "id": "7f6541d7-affc-403b-a04a-735c5231bf5e",
            "lob": "ckcoeuat",
            "changed": true,
            "bannerElements": [
              {
                "id": "1c59d1e1-f3a8-4417-9766-3fdeec81508a",
                "elementType": "image",
                "extendedAttributes": {
                  "title": "Enjoy Coca Cola!",
                  "subtitle": "Boost customer connect and sales with Coca-Cola.",
                  "buttonText": "Watch Now",
                  "brand": "Indica"
                },
                "activeStatus": "active",
                "elementNumber": "1",
                "elementTitle": "Element One",
                "mediaUrl": "https://youtu.be/d21aCCMKRVw?si=r2J8QLMEu88L7Lw-",
                "mediaName": "youtubeLink"
              },
              {
                "id": "6c64d0b3-62b6-461a-a631-de5f775335fe",
                "elementType": "image",
                "extendedAttributes": {
                  "title": "Sprite, Obey your thirst!",
                  "subtitle": "Give your customers the taste of real, refreshing sprite.",
                  "buttonText": "Watch Now",
                  "brand": "CavinKare"
                },
                "activeStatus": "active",
                "elementNumber": "2",
                "elementTitle": "Element Two",
                "mediaUrl": "https://youtu.be/E3103h-DmgE?si=kyE4uR2jmBqHgGsT",
                "mediaName": "youtubeLink"
              }
            ]
          }
        ]
    
    

      }
    }
    let data: any = fallbackData;
    
    try {
      const response = await fetch('https://e0mw1.wiremockapi.cloud/banners');
      if (response.ok) {
        const apiData: any = await response.json();
        if (apiData && apiData.features && typeof apiData.features === 'object') {
          data = apiData;
        } else {
          data = fallbackData;
        }
      } else {
        data = fallbackData;
      }
    } catch (apiError) {
      data = fallbackData;
    }

    if (!data.features || typeof data.features !== 'object') {
      return { promoBanners: [], videoAds: [] };
    }

    // Get current LOB to filter the appropriate features
    const currentLob = EnvironmentRegistry.getCurrentLob();
    
    if (!currentLob) {
      return { promoBanners: [], videoAds: [] };
    }
    
    // Remove "demo" or "uat" suffix from LOB name to get the base LOB
    const baseLob = currentLob.replace(/(demo|uat)$/i, '');
    
    const lobFeatures = data.features[baseLob];

    if (!lobFeatures || !Array.isArray(lobFeatures)) {
      return { promoBanners: [], videoAds: [] };
    }

    const promoBanners: PromoBannerData[] = [];
    const videoAds: VideoAdData[] = [];

    lobFeatures.forEach((feature: any) => {
      if (feature.activeStatus === 'active') {
        if (feature.bannerType === 'template1') {
          // Process PromoBanner data - sort by elementNumber
          const sortedElements = feature.bannerElements
            .filter((element: any) => element.activeStatus === 'active' && element.blobKey)
            .sort((a: any, b: any) => {
              const aNum = parseInt(a.elementNumber) || 0;
              const bNum = parseInt(b.elementNumber) || 0;
              return aNum - bNum;
            });

          sortedElements.forEach((element: any) => {
            promoBanners.push({
              id: element.id,
              elementType: element.elementType,
              elementTitle: element.elementTitle,
              blobKey: element.blobKey,
              mediaUrl: element.mediaUrl,
              mediaName: element.mediaName,
              fileName: element.fileName
            });
          });
        } else if (feature.bannerType === 'template20') {
          // Process VideoAd data - sort by elementNumber
          const sortedElements = feature.bannerElements
            .filter((element: any) => element.activeStatus === 'active' && element.mediaUrl)
            .sort((a: any, b: any) => {
              const aNum = parseInt(a.elementNumber) || 0;
              const bNum = parseInt(b.elementNumber) || 0;
              return aNum - bNum;
            });

          sortedElements.forEach((element: any) => {
            videoAds.push({
              id: element.id,
              title: element.extendedAttributes?.title || '',
              subtitle: element.extendedAttributes?.subtitle || '',
              buttonText: element.extendedAttributes?.buttonText || '',
              videoUrl: element.mediaUrl!,
              brand: element.extendedAttributes?.brand || ''
            });
          });
        }
      }
    });

    const result = { promoBanners, videoAds };

    // Cache the result for 20 seconds
    setCachedData(CACHE_KEYS.BANNER_DATA, result);

    return result;
  } catch (error) {
    console.error('Error fetching banner data:', error);
    return { promoBanners: [], videoAds: [] };
  }
};

export const getCachedCollectionData = async (outletCode?: string, creditStatus?: string) => {
  
  const waitForOutlets = async (): Promise<OutletDetails[]> => {
    return new Promise((resolve) => {
      const checkOutlets = async () => {
        const outletData = await getDataFromLocalDb("OutletList");
        const allOutlets: OutletDetails[] = outletData.data;
        
        if (allOutlets && allOutlets.length > 0) {
          resolve(allOutlets);
        } else {
          setTimeout(checkOutlets, 50);
        }
      };
      
      checkOutlets();
    });
  };

  await waitForOutlets();

  const user = JSON.parse(localStorage.getItem("authContext") || '{}')?.user?.loginId || '';
  const cacheKey = `${CACHE_KEYS.PRODUCT_DATA}_collection_${user}_${outletCode || 'all'}_${creditStatus || 'all'}`;

  // Check cache first
  const cachedData = getCachedData<any[]>(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  try {
    const result = await fetchAllData(outletCode, creditStatus);

    // Cache the result for 20 seconds
    setCachedData(cacheKey, result);

    return result;
  } catch (error) {
    throw error;
  }
};

export const getEnrichedOutOfStockDataAlternative = async (api1Endpoint: string): Promise<EnrichedResponse[]> => {
  try {

    const lob = EnvironmentRegistry.getCurrentLob();
    const authContext = localStorage.getItem("authContext");
    const user = authContext ? JSON.parse(authContext).user.loginId : '';

    const url = `https://datanode.saleslens.salescode.ai/skustockinfo/?accountId=${lob}&loginId=${encodeURIComponent(user)}`;

    // Create cache key for the main SKU stock data
    const cacheKey = `${CACHE_KEYS.PRODUCT_DATA}_skustockinfo_${user}`;

    // Check cache first
    const cachedData = getCachedData<EnrichedResponse[]>(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const response = await axios.get(url, {
      headers: {
        'accept': 'application/json, text/plain, */*',
        'accept-language': 'en-US,en;q=0.9,hi;q=0.8,eo;q=0.7,te;q=0.6',
      }
    });

    const api1Data: Api1Response[] = response.data;
    
    // Step 3: Validate API1 response data
    if (!api1Data || !Array.isArray(api1Data) || api1Data.length === 0) {

      return [];
    }
    
    // Step 4: Filter out items with invalid skucodes
    const validApi1Data = api1Data.filter(item => 
      item && 
      typeof item === 'object' && 
      item.skucode && 
      typeof item.skucode === 'string' && 
      item.skucode.trim() !== ''
    );
    
    if (validApi1Data.length === 0) {

      return [];
    }
    
    // Step 5: Sort by avgDailyOrder and get top 3 SKUs
    const sortedByAvgDailyOrder = validApi1Data
      .sort((a, b) => (b.avgDailyOrder || 0) - (a.avgDailyOrder || 0));

    const top3Skus = sortedByAvgDailyOrder.slice(0, 3);
    const top3SkuCodes = top3Skus.map(item => item.skucode).filter(code => code && code.trim() !== '');

    // Step 6: Get configuration and determine pricing fetch method
    // NOT IN CURRENT USE
    
    // Step 7: Get batch pricing data for top 3 SKUs
    const pricingCacheKey = `${CACHE_KEYS.PRODUCT_DATA}_batch_pricing_${user}_${top3SkuCodes.join('_')}`;
    const cachedPricingData = getCachedData<any>(pricingCacheKey);

    let pricingMap: Map<string, any>;
    if (cachedPricingData && typeof cachedPricingData === 'object') {
      // Convert cached object back to Map
      pricingMap = new Map(Object.entries(cachedPricingData));
    } else {
      pricingMap = await getBatchPricingData(top3SkuCodes);
      // Cache the pricing map (convert to object for storage)
      const pricingObject = Object.fromEntries(pricingMap);
      setCachedData(pricingCacheKey, pricingObject);
    }

    // Step 8: Extract unique skucodes for batch query
    const uniqueSkucodes = Array.from(new Set(validApi1Data.map(item => item.skucode && typeof item.skucode === 'string' ? item.skucode.trim() : '')));

    if (uniqueSkucodes.length === 0) {
      return [];
    }

    // Step 9: Fetch all products instead of filtering by skucodes
    const productCacheKey = `${CACHE_KEYS.PRODUCT_DATA}_products_${user}`;
    const cachedProductData = getCachedData<any>(productCacheKey);

    let productResponse: any;
    if (cachedProductData) {
      productResponse = cachedProductData;
    } else {
      productResponse = await getDataFromLocalDb("Product");
      // Cache the product data
      setCachedData(productCacheKey, productResponse);
    }

    // Step 10: Validate product response
    if (!productResponse || !productResponse.data || !Array.isArray(productResponse.data)) {
      // Return data without enrichment if product query fails
      return validApi1Data.map(item => ({
        ...item,
        blobKey: '',
        skuName: '',
        packPtr: 0
      }));
    }

    // Extract the actual product data from the nested structure
    const productData = productResponse.data?.[0]?.data || {};


    // Step 11: Create a map for O(1) lookup performance with null checks
    const productMap = new Map<string, any>();

    // productData is an object with SKU codes as keys, so we need to iterate over its values
    if (productData && typeof productData === 'object') {
      Object.values(productData).forEach((product: any) => {
        if (product && typeof product === 'object') {
          const skuCode = product.skuCode || product.skucode;
          if (skuCode && typeof skuCode === 'string' && skuCode.trim() !== '') {
            productMap.set(skuCode.trim(), product);
          }
        }
      });
    }

    // Step 12: Enrich all items using the maps with comprehensive null checks
    const enrichedData: EnrichedResponse[] = validApi1Data.map(item => {
      if (!item || typeof item !== 'object') {
        return {
          skucode: '',
          outOfStockTime: '',
          outOfStockDays: 0,
          avgDailyOrder: 0,
          blobKey: '',
          skuName: '',
          packPtr: 0
        };
      }

      const skuCode = item.skucode && typeof item.skucode === 'string' ? item.skucode.trim() : '';
      const productInfo = skuCode ? productMap.get(skuCode) : null;
      const pricingInfo = skuCode ? pricingMap.get(skuCode) : null;

      return {
        ...item,
        blobKey: (productInfo?.blobKey || productInfo?.extendedAttributes?.blobKey) || '',
        skuName: (productInfo?.skuName || productInfo?.skuDescription || productInfo?.name) || '',
        packPtr: (pricingInfo?.packPtr && typeof pricingInfo.packPtr === 'number') ? pricingInfo.packPtr :
                (pricingInfo?.packPtr && typeof pricingInfo.packPtr === 'string') ? parseFloat(pricingInfo.packPtr) || 0 : 0
      };
    });
    // Cache the result for 20 seconds
    setCachedData(cacheKey, enrichedData);

    return enrichedData;

  } catch (error) {
    throw error;
  }
};
